import type { AdminOrderPayoutDto } from '@carcheck/shared';
import Chip from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoGrid, InfoItem } from './InfoBlocks';
import { formatCurrency, formatDateTime } from '../../../utils/format';

/**
 * Payout block of the Платёж tab (AUT-123; extracted from `PaymentTab` in
 * AUT-219 so the tab body stays a composer).
 *
 * Read-only here on purpose: the payout retry action lives on
 * `/admin/payouts` and is NOT duplicated into the order drawer — one
 * destructive "cancel + replace" trigger, one place it can be pressed.
 *
 * Already sanitised server-side — recipient last-4 only, never PAN /
 * recurring_token (architect lock AUT-112).
 */
export const PayoutSummary = ({ payout }: { payout: AdminOrderPayoutDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <InfoGrid>
      <InfoItem label={t('orders.detail.payout.status')}>
        <Chip size="small" label={payout.status} />
      </InfoItem>
      <InfoItem label={t('orders.detail.payout.amount')}>
        {formatCurrency(Number(payout.amount), payout.currency)}
      </InfoItem>
      <InfoItem label={t('orders.detail.payout.recipientCard')}>
        {payout.recipientCardLast4 ? `···· ${payout.recipientCardLast4}` : '—'}
      </InfoItem>
      <InfoItem label={t('orders.detail.payout.scheduledFor')}>
        {formatDateTime(payout.scheduledFor)}
      </InfoItem>
      <InfoItem label={t('orders.detail.payout.processedAt')}>
        {formatDateTime(payout.processedAt)}
      </InfoItem>
      {payout.failedReason ? (
        <InfoItem label={t('orders.detail.payout.failedReason')} span>
          {payout.failedReason}
        </InfoItem>
      ) : null}
    </InfoGrid>
  );
};
